import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Injectable, Injector, Renderer2, RendererFactory2 } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { Roles } from '../core/ucr-security/roles';
import { UcrSecurityService } from '../core/ucr-security/ucr-security.service';
import { UserInfo } from '../core/ucr-security/user-info';
import { GenericFormGroupComponent } from '../forms/generic-form-group.component';
import { LogService } from '../shared/services/log.service';
import { DialogDataService } from './dialog-data.service';
import { DialogWrapperComponent } from './dialog-wrapper.component';
import { DialogConfig, DialogData, DialogReference, DialogResponse } from './dialog.types';

@Injectable({
    providedIn: 'root'
})
export class DialogService<T> {
    private dialogRefs: Array<DialogReference>;
    private subject = new Subject<DialogResponse<T>>();
    public response = this.subject.asObservable();
    private renderer2: Renderer2;
    private userInfo: UserInfo;
    private enableMultiWindow: boolean;

    constructor(
        private dataService: DialogDataService<T>,
        private security: UcrSecurityService,
        private injector: Injector,
        private dialog: MatDialog,
        private rendererFactory: RendererFactory2,
        private logger: LogService
    ) {
        this.renderer2 = rendererFactory.createRenderer(null, null);
        this.security.getUserInfo().subscribe((userInfo: UserInfo) => {
            this.userInfo = userInfo;
            this.enableMultiWindow = this.userInfo.ifAnyGranted([
                Roles.siteAdmin,
                Roles.registrarAdmin,
                Roles.registrarStaff
            ]);
        });
    }

    updateTitle(uniqueId: string, title: string) {
        const ref = this.findDialogRef(uniqueId);
        if (ref?.componentInstance?.data && title) {
            ref.componentInstance.data.title = title;
        }
    }

    setTitleListener(uniqueId: string, control: UntypedFormControl, suffix?: string, prefix?: string) {
        const ref = this.findDialogRef(uniqueId);
        if (ref?.componentInstance) {
            ref.componentInstance.setTitleControlListener(control, suffix, prefix);
        }
    }

    updateUniqueId(uniqueId: string, newUniqueId: string) {
        const ref = this.findIdialogRef(uniqueId);
        ref.uniqueId = newUniqueId;
    }

    generateUniqueId(element: any, property: string, typeName: string) {
        if (!element) {
            return `new_${typeName}`;
        }
        return `${typeName}_${element[property]}`;
    }

    private createInjector(value: T, dialogAlias: string, uniqueId: string, injector: Injector) {
        return Injector.create({
            providers: [
                {
                    provide: DialogDataService,
                    useValue: {
                        dialogAlias: dialogAlias,
                        uniqueId: uniqueId,
                        data: value
                    }
                }
            ],
            parent: injector
        });
    }

    private cleanUp() {
        this.dialogRefs = this.dialogRefs.filter(
            (m) => m.dialogRef.getState() === MatDialogState.OPEN || m.dialogRef.componentInstance
        );
    }

    private reOrderWindows() {
        try {
            let startZ = 1000;
            this.dialogRefs
                .sort((a, b) => {
                    return a.zIndex - b.zIndex;
                })
                .forEach((ref) => {
                    if (this.changeZIndexTo(ref, startZ)) {
                        ++startZ;
                    }
                });
        } catch (e) {
            this.logger.logInfo(e);
        }
    }

    private bringToFront(index: number, dialogRef: MatDialogRef<DialogWrapperComponent>) {
        try {
            if (this.dialogRefs.length === 1) {
                return;
            }
            if (dialogRef?.componentInstance?.wrapper?.nativeElement) {
                const highestZ = this.dialogRefs
                    .map((m) => m.zIndex)
                    .sort((a, b) => {
                        return b - a;
                    })[0];
                if (this.dialogRefs[index].zIndex < highestZ || (highestZ === 1000 && this.dialogRefs.length > 1)) {
                    const highestIndex = this.dialogRefs.map((m) => m.zIndex).indexOf(highestZ);
                    this.changeZIndexTo(this.dialogRefs[highestIndex], highestZ - 0.5);
                    this.changeZIndexTo(this.dialogRefs[index], highestZ);
                }
            }
            this.reOrderWindows();
        } catch (e) {
            this.logger.logInfo(e);
        }
    }

    private changeZIndexTo(ref: DialogReference, zIndex: number): boolean {
        try {
            if (ref?.dialogRef?.componentInstance?.wrapper?.nativeElement) {
                const overlay =
                    ref.dialogRef.componentInstance.wrapper.nativeElement.closest('.cdk-global-overlay-wrapper');
                this.renderer2.setStyle(overlay, 'z-index', zIndex);
                ref.zIndex = zIndex;
                return true;
            }
        } catch (e) {
            this.logger.logInfo(e);
        }
        return false;
    }

    private destroyDialogReference(index: number) {
        try {
            if (index >= 0) {
                const ref = this.dialogRefs[index];
                if (ref.clickSubscription) {
                    ref.clickSubscription.unsubscribe();
                }
                if (ref.openSubscription) {
                    ref.openSubscription.unsubscribe();
                }
                if (ref.closeSubscription) {
                    ref.closeSubscription.unsubscribe();
                }
                this.dialogRefs.splice(index, 1);
            }
        } catch (e) {
            this.logger.logInfo(e);
        }
    }

    private exists(value: string): boolean {
        try {
            const index = this.dialogRefs.map((m) => m.uniqueId).indexOf(value);
            if (index >= 0) {
                const dialogRef = this.dialogRefs[index].dialogRef;
                if (dialogRef && dialogRef.getState() === MatDialogState.OPEN) {
                    this.bringToFront(index, dialogRef);
                    return true;
                } else if (
                    !dialogRef ||
                    dialogRef.getState() === MatDialogState.CLOSED ||
                    dialogRef.getState() === MatDialogState.CLOSING
                ) {
                    this.destroyDialogReference(index);
                    return false;
                }
            }
        } catch (e) {
            this.logger.logInfo(e);
            return false;
        }
    }

    private store(uniqueId: string, dialogRef: MatDialogRef<DialogWrapperComponent>): void {
        const newRef = {
            uniqueId: uniqueId,
            zIndex: this.dialogRefs.length + 1000,
            dialogRef: dialogRef
        } as DialogReference;
        if (this.enableMultiWindow) {
            newRef.openSubscription = dialogRef.afterOpened().subscribe((next) => {
                const dRef = this.dialogRefs[this.dialogRefs.length - 1];
                this.changeZIndexTo(dRef, dRef.zIndex);
                dRef.clickSubscription = dRef.dialogRef.componentInstance.panelClickedEvent.subscribe(
                    (next: string) => {
                        const index = this.dialogRefs.map((x) => x.uniqueId).indexOf(next);
                        if (index >= 0) {
                            this.bringToFront(index, this.dialogRefs[index].dialogRef);
                        }
                    }
                );
            });
        }
        newRef.closeSubscription = dialogRef.afterClosed().subscribe((next: string) => {
            this.remove(next);
        });
        this.dialogRefs.push(newRef);
    }

    private remove(uniqueId: string) {
        if (!uniqueId) {
            return;
        }
        try {
            const index = this.dialogRefs.map((x) => x.uniqueId).indexOf(uniqueId);
            this.destroyDialogReference(index);
        } catch (e) {
            this.logger.logInfo(e);
        }
    }

    show(value: T, dialogConfig: DialogConfig): MatDialogRef<DialogWrapperComponent> {
        if (!this.dialogRefs) {
            this.dialogRefs = [];
        }
        this.cleanUp();
        if (this.enableMultiWindow) {
            if (this.exists(dialogConfig.uniqueId)) {
                return;
            }
        }

        const injector: Injector = this.createInjector(
            value,
            dialogConfig.dialogAlias,
            dialogConfig.uniqueId,
            this.injector
        );

        const data: DialogData<T> = {
            uniqueId: dialogConfig.uniqueId,
            title: dialogConfig.title,
            component: dialogConfig.component,
            injector: injector,
            value: value,
            refreshAction: dialogConfig.refreshAction,
            enableMultiWindow: this.enableMultiWindow
        };
        const matDialogConfig: MatDialogConfig = {
            data: data,
            panelClass: 'ucr-panel',
            hasBackdrop: !this.enableMultiWindow,
            disableClose: true,
            closeOnNavigation: true,
            scrollStrategy: this.enableMultiWindow ? new NoopScrollStrategy() : undefined
        };
        const dialogRef: MatDialogRef<DialogWrapperComponent> = this.dialog.open(
            DialogWrapperComponent,
            matDialogConfig
        );
        this.store(dialogConfig.uniqueId, dialogRef);
        return dialogRef;
    }

    private findIdialogRef(uniqueId: string): DialogReference {
        try {
            const index = this.dialogRefs.map((m) => m.uniqueId).indexOf(uniqueId);
            if (index >= 0) {
                return this.dialogRefs[index];
            }
            return undefined;
        } catch (e) {
            this.logger.logInfo(e);
        }
    }

    private findDialogRef(uniqueId: string): MatDialogRef<DialogWrapperComponent> {
        try {
            const d = this.findIdialogRef(uniqueId);
            return d?.dialogRef;
        } catch (e) {
            this.logger.logInfo(e);
        }
    }

    addFormGroup(group: GenericFormGroupComponent, uniqueId: string) {
        const d = this.findDialogRef(uniqueId);
        if (d?.componentInstance) {
            d.componentInstance.addFormGroup(group);
        }
    }

    removeFormGroup(where: string, uniqueId: string) {
        const d = this.findDialogRef(uniqueId);
        if (d?.componentInstance) {
            d.componentInstance.removeWhere(where);
        }
    }

    send(dataService: DialogDataService<T>, obj: T) {
        this.subject.next({
            dialogAlias: dataService.dialogAlias,
            payload: obj
        } as DialogResponse<T>);
    }
}
